import type { Cookie, SameSite } from '@core/entities/Cookie';
import type { CookiePayload, CookieRepository } from '@core/repositories/CookieRepository';
import { httpClient } from '@infrastructure/http/httpClient';
import { toAppError } from '@infrastructure/http/errorMapper';

interface CookieResponse {
  id: string;
  domain: string;
  name: string;
  value: string;
  path: string;
  expires_at: string | null;
  is_http_only: boolean;
  is_secure: boolean;
  same_site: SameSite | null;
  created_at: string;
  updated_at: string;
}

const toCookie = (payload: CookieResponse): Cookie => ({
  id: payload.id,
  domain: payload.domain,
  name: payload.name,
  value: payload.value,
  path: payload.path,
  expiresAt: payload.expires_at,
  isHttpOnly: payload.is_http_only,
  isSecure: payload.is_secure,
  sameSite: payload.same_site,
  createdAt: payload.created_at,
  updatedAt: payload.updated_at,
});

const toPayloadBody = (payload: CookiePayload) => ({
  domain: payload.domain,
  name: payload.name,
  value: payload.value,
  path: payload.path ?? '/',
  expires_at: payload.expiresAt ?? null,
  is_http_only: payload.isHttpOnly ?? false,
  is_secure: payload.isSecure ?? false,
  same_site: payload.sameSite ?? null,
});

export class CookieRepositoryImpl implements CookieRepository {
  async list(workspaceId: string): Promise<Cookie[]> {
    try {
      const response = await httpClient.get<{ data: CookieResponse[] }>(
        `/workspaces/${workspaceId}/cookies`,
      );
      return response.data.data.map(toCookie);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async create(workspaceId: string, payload: CookiePayload): Promise<Cookie> {
    try {
      const response = await httpClient.post<{ data: CookieResponse }>(
        `/workspaces/${workspaceId}/cookies`,
        toPayloadBody(payload),
      );
      return toCookie(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async update(id: string, payload: CookiePayload): Promise<Cookie> {
    try {
      const response = await httpClient.put<{ data: CookieResponse }>(
        `/cookies/${id}`,
        toPayloadBody(payload),
      );
      return toCookie(response.data.data);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async remove(id: string): Promise<void> {
    try {
      await httpClient.delete(`/cookies/${id}`);
    } catch (error) {
      throw toAppError(error);
    }
  }

  async clear(workspaceId: string): Promise<void> {
    try {
      await httpClient.delete(`/workspaces/${workspaceId}/cookies`);
    } catch (error) {
      throw toAppError(error);
    }
  }
}
